import { useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Request, Procedure } from "@interfaces";
import { RequestType, requestTypeNavigateParser } from "@interfaces";
import { api, queryClient } from "@contexts";
import { useMutation } from "@tanstack/react-query";
import { parseProcedureSuggestions } from "@screens/MedicalOrder/utils/parse";
import { useDoctorDocumentReadiness } from "../useDoctorDocumentReadiness";

interface RepeatRequestResponse {
  request: Request;
  sourceDocumentData: Record<string, unknown> | null;
}

function buildNavigationState(
  documentType: RequestType,
  sourceDocumentData: Record<string, unknown> | null,
): Record<string, unknown> {
  if (!sourceDocumentData) return {};

  switch (documentType) {
    case RequestType.COMPOUNDED_PRESCRIPTION:
      return sourceDocumentData;
    case RequestType.MEDICAL_CERTIFICATE:
      return {
        repeatedDocumentDetails: sourceDocumentData.documentDetails,
      };
    case RequestType.MEDICAL_ORDER:
      return {
        repeatedDiagnosticHypothesis: sourceDocumentData.diagnosticHypothesis,
        // A medical order's details: its procedures.
        repeatedDocumentDetails: (
          sourceDocumentData.documentDetails as
            { description: string; code?: string }[] | undefined
        )?.map((detail) => ({
          text: detail.code
            ? parseProcedureSuggestions({
                description: detail.description,
                code: detail.code,
              } as Procedure)
            : detail.description,
          code: detail.code ?? null,
        })),
        repeatedIndications: sourceDocumentData.indications,
      };
    default:
      return {};
  }
}

export const useRepeatRequest = () => {
  const navigate = useNavigate();
  const { requestId: sourceRequestId = "" } = useParams();
  const { checkSigningBlock, modalProps } = useDoctorDocumentReadiness();
  // The button is only disabled once the mutation starts, so a second click
  // during the block check would repeat the request twice.
  const isRepeating = useRef(false);

  const { mutateAsync: repeatRequest, isPending: isRepeatingDocument } = useMutation({
    mutationFn: (sourceRequestId: string) =>
      api<RepeatRequestResponse>({
        method: "POST",
        url: `/requests/${sourceRequestId}/repeat`,
      }),
    onSuccess: ({ request, sourceDocumentData }) => {
      const { id: newRequestId, documentType } = request;

      queryClient.invalidateQueries({
        queryKey: ["request", newRequestId],
      });

      const navigationState = buildNavigationState(documentType, sourceDocumentData);

      navigate(
        `/app/solicitudes/${newRequestId}/${requestTypeNavigateParser[documentType]}/crear`,
        { state: navigationState },
      );
    },
    onError: (error) => {
      console.error("[useRepeatRequest] Error al repetir solicitud", {
        error,
        sourceRequestId,
      });
    },
  });

  const handleRepeatRequest = useCallback(async () => {
    if (!sourceRequestId || isRepeating.current) return;
    isRepeating.current = true;
    try {
      // Repeating creates a new request, so a doctor past their deadline is
      // stopped before it exists.
      if (await checkSigningBlock()) return;
      await repeatRequest(sourceRequestId);
    } catch {
      // The mutation's onError already logs it.
    } finally {
      isRepeating.current = false;
    }
  }, [sourceRequestId, repeatRequest, checkSigningBlock]);

  return {
    handleRepeatRequest,
    isRepeatingDocument,
    /** For the modal that stops a doctor past their deadline. */
    modalProps,
  };
};
